// Import Verification & Reconciliation System Types

export type ImportType = 'careem' | 'uber' | 'salik' | 'fines';

export type ImportStatus = 
  | 'pending' 
  | 'processing' 
  | 'completed' 
  | 'failed' 
  | 'verification_failed';

export interface ImportSnapshot {
  totalAmount: number;
  recordCount: number;
  checksum: string;
  capturedAt: string;
  driverBreakdown: DriverSnapshot[];
}

export interface DriverSnapshot {
  driverId: string;
  driverName: string;
  totalAmount: number;
  recordCount: number;
  lastTransactionDate?: string;
}

export interface CSVAnalysis {
  totalAmount: number;
  recordCount: number;
  checksum: string;
  driverBreakdown: DriverSnapshot[];
  duplicatesFound: DuplicateRecord[];
  validRecords: any[];
  rejectedRecords: any[];
}

export interface DuplicateRecord {
  csvRowNumber: number;
  csvData: any;
  duplicateCriteria: string;
  existingRecordId: string;
  existingRecordData: any;
  rejectionReason: string;
}

export interface VerificationResult {
  passed: boolean;
  summary: VerificationSummary;
  driverDetails: DriverVerification[];
  duplicateLog: DuplicateRecord[];
  discrepancies: Discrepancy[];
}

export interface VerificationSummary {
  // Totals
  expectedAmountIncrease: number;
  actualAmountIncrease: number;
  amountDiscrepancy: number;
  
  // Counts
  expectedCountIncrease: number;
  actualCountIncrease: number;
  countDiscrepancy: number;
  
  // Processing stats
  csvRecordsProcessed: number;
  recordsImported: number;
  recordsRejected: number;
  duplicatesFound: number;
  
  // Verification status
  amountVerificationPassed: boolean;
  countVerificationPassed: boolean;
  overallVerificationPassed: boolean;
}

export interface DriverVerification {
  driverId: string;
  driverName: string;
  
  preAmount: number;
  postAmount: number;
  csvAmount: number;
  
  preCount: number;
  postCount: number;
  csvCount: number;
  
  expectedAmountChange: number;
  actualAmountChange: number;
  amountDiscrepancy: number;
  
  expectedCountChange: number;
  actualCountChange: number;
  countDiscrepancy: number;
  
  verificationPassed: boolean;
  discrepancyNotes: string[];
}

export interface Discrepancy {
  type: 'amount' | 'count' | 'checksum' | 'driver_specific';
  severity: 'critical' | 'warning' | 'info';
  description: string;
  expected: any;
  actual: any;
  difference: any;
  driverId?: string;
  suggestions: string[];
}

export interface ImportHistory {
  id: string;
  importType: ImportType;
  importDate: string;
  importedBy?: string;
  fileName?: string;
  status: ImportStatus;
  
  // Snapshots
  preSnapshot: ImportSnapshot;
  csvAnalysis: CSVAnalysis;
  postSnapshot: ImportSnapshot;
  
  // Verification
  verificationResult: VerificationResult;
  
  // Metrics
  processingTimeMs: number;
  
  createdAt: string;
  updatedAt: string;
}

export interface ImportVerificationConfig {
  importType: ImportType;
  duplicateCriteria: string[]; // e.g., ['date', 'driver_id', 'amount']
  toleranceAmount: number; // Acceptable discrepancy in AED
  tolerancePercentage: number; // Acceptable discrepancy in %
  requireExactMatch: boolean;
}

// Specific import configurations
export const IMPORT_VERIFICATION_CONFIGS: Record<ImportType, ImportVerificationConfig> = {
  careem: {
    importType: 'careem',
    duplicateCriteria: ['trip_date', 'driver_id', 'net_fare'],
    toleranceAmount: 1.0, // AED 1 tolerance
    tolerancePercentage: 0.1, // 0.1% tolerance
    requireExactMatch: false
  },
  uber: {
    importType: 'uber',
    duplicateCriteria: ['trip_date', 'driver_id', 'fare_amount'],
    toleranceAmount: 1.0,
    tolerancePercentage: 0.1,
    requireExactMatch: false
  },
  salik: {
    importType: 'salik',
    duplicateCriteria: ['transaction_date', 'driver_id', 'amount'],
    toleranceAmount: 0.5, // Smaller tolerance for tolls
    tolerancePercentage: 0.05,
    requireExactMatch: true
  },
  fines: {
    importType: 'fines',
    duplicateCriteria: ['fine_date', 'driver_id', 'amount'],
    toleranceAmount: 0.0, // Exact match required for fines
    tolerancePercentage: 0.0,
    requireExactMatch: true
  }
};

// Helper types for API responses
export interface ImportVerificationResponse {
  success: boolean;
  importHistoryId: string;
  verificationResult: VerificationResult;
  message: string;
}

export interface ImportHistoryResponse {
  imports: ImportHistory[];
  totalCount: number;
  page: number;
  pageSize: number;
}
